/**
 * Organizations API Slice
 *
 * This module provides RTK Query endpoints for organization management.
 * It uses the auto-generated endpoints from the OpenAPI spec and re-exports
 * them with user-friendly names.
 */

import { enhancedApi } from '@store/generated/organizations';

/**
 * Organizations API - enhanced from generated endpoints with cache tag invalidation
 */
export const organizationsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: [
    'Subscription',
    'Members',
    'Invitations',
    'SubscriptionDetails',
    'UserInfo',
    'Invoices',
    'Entitlements',
    'PermissionsMatrix',
    'Organization',
  ],
  endpoints: {
    // Organization info card (name, member counts)
    getV2ProviderOrganizations: { providesTags: ['Organization'] },

    // Members endpoints - provide/invalidate members list cache
    getV2ProviderOrganizationsMembers: {
      providesTags: ['Members'],
    },
    patchV2ProviderOrganizationsMembersByUserIdProfile: {
      invalidatesTags: ['Members'],
    },
    // Org preferences toggle (e.g. block signing until placeholders filled) —
    // invalidate UserInfo so the resolved effective_organization_preferences
    // refetches and the change takes effect immediately in the note editor.
    patchV2ProviderOrganizationsPreferences: {
      invalidatesTags: ['UserInfo'],
    },
    patchV2ProviderOrganizationsMembersByUserIdBillingInfo: {
      invalidatesTags: ['Members'],
    },

    // Membership info (co-sign settings)
    getV2ProviderOrganizationsMembersByUserIdMembershipInfo: {
      providesTags: (_result, _error, arg) => [
        { type: 'Members' as const, id: `membership-${arg.userId}` },
      ],
    },
    patchV2ProviderOrganizationsMembersByUserIdMembershipInfo: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Members' as const, id: `membership-${arg.userId}` },
      ],
    },

    // Member bundle management - invalidate subscription details and entitlements when bundles are added
    postV2ProviderOrganizationsMembersByUserIdBundles: {
      invalidatesTags: ['Subscription', 'SubscriptionDetails', 'UserInfo', 'Invoices', 'Entitlements'],
    },
    // Member bundle change - invalidate subscription details and entitlements when bundle is changed
    putV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentId: {
      invalidatesTags: ['Subscription', 'SubscriptionDetails', 'UserInfo', 'Invoices', 'Entitlements'],
    },
    // Member bundle removal - invalidate subscription details and entitlements when bundle is removed
    deleteV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentId: {
      invalidatesTags: ['Subscription', 'SubscriptionDetails', 'UserInfo', 'Invoices', 'Entitlements'],
    },

    // Invitations — the pending-invitations list. Sending an invite flips the
    // org to a group (organization_info.group → true), so create/delete also
    // invalidate UserInfo to refresh the flag (drives the Members tab + Nav
    // link visibility + the solo-vs-group subscription view) and Members so
    // the table reflects the change. UserInfo on delete covers the case where
    // removing the last invite reverts a fresh org back to solo.
    getV2ProviderOrganizationsInvitations: { providesTags: ['Invitations'] },
    postV2ProviderOrganizationsInvitations: {
      invalidatesTags: ['Invitations', 'UserInfo', 'Members'],
    },
    patchV2ProviderOrganizationsInvitationsByInvitationId: {
      invalidatesTags: ['Invitations'],
    },
    deleteV2ProviderOrganizationsInvitationsByInvitationId: {
      invalidatesTags: ['Invitations', 'UserInfo', 'Members'],
    },
    postV2ProviderOrganizationsInvitationsByInvitationIdResend: {
      invalidatesTags: ['Invitations'],
    },

    // Org rename (GDN-2441): the name feeds staff surfaces and the public
    // booking page; refetch the org info card after a rename.
    patchV2ProviderOrganizations: { invalidatesTags: ['Organization'] },

    // Permissions matrix (ADR 0073) - query provides the matrix cache; the
    // mutation invalidates the matrix and UserInfo so derived permissions refetch.
    getV2ProviderOrganizationsPermissions: { providesTags: ['PermissionsMatrix'] },
    patchV2ProviderOrganizationsPermissions: {
      invalidatesTags: ['PermissionsMatrix', 'UserInfo'],
    },
  },
});

// Export hooks with user-friendly names for provider endpoints
export const {
  // Organization info
  useGetV2ProviderOrganizationsQuery: useGetOrganizationQuery,
  usePatchV2ProviderOrganizationsMutation: useRenameOrganizationMutation,

  // Members management
  useGetV2ProviderOrganizationsMembersQuery: useGetOrganizationMembersQuery,
  useGetV2ProviderOrganizationsMembersByUserIdDetailQuery: useGetMemberDetailQuery,
  useDeleteV2ProviderOrganizationsMembersByUserIdMutation: useRemoveMemberMutation,
  usePostV2ProviderV2ProviderOrganizationsMembersByUserIdReactivateMutation:
    useReactivateMemberMutation,
  usePatchV2ProviderOrganizationsMembersByUserIdRolesMutation: useUpdateMemberRolesMutation,
  usePatchV2ProviderOrganizationsMembersByUserIdProfileMutation: useUpdateMemberProfileMutation,
  usePatchV2ProviderOrganizationsPreferencesMutation: useUpdateOrganizationPreferencesMutation,
  usePatchV2ProviderOrganizationsMembersByUserIdBillingInfoMutation: useUpdateMemberBillingInfoMutation,
  useGetV2ProviderOrganizationsMembersByUserIdMembershipInfoQuery: useGetMembershipInfoQuery,
  usePatchV2ProviderOrganizationsMembersByUserIdMembershipInfoMutation: useUpdateMembershipInfoMutation,

  // Member bundle management
  usePostV2ProviderOrganizationsMembersByUserIdBundlesMutation: useAddMemberBundleMutation,
  usePutV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentIdMutation:
    useChangeMemberBundleMutation,
  useDeleteV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentIdMutation:
    useRemoveMemberBundleMutation,

  // Invitations management
  useGetV2ProviderOrganizationsInvitationsQuery: useGetInvitationsQuery,
  usePostV2ProviderOrganizationsInvitationsMutation: useCreateInvitationMutation,
  usePatchV2ProviderOrganizationsInvitationsByInvitationIdMutation: useUpdateInvitationMutation,
  useDeleteV2ProviderOrganizationsInvitationsByInvitationIdMutation: useDeleteInvitationMutation,
  usePostV2ProviderOrganizationsInvitationsByInvitationIdResendMutation:
    useResendInvitationMutation,

  // Invitation acceptance (used by accept-invitation feature)
  useGetV2ProviderOrganizationsInvitationsValidateByTokenQuery: useValidateInvitationQuery,
  usePostV2ProviderOrganizationsInvitationsAcceptMutation: useAcceptInvitationMutation,

  // Roles
  useGetV2ProviderOrganizationsRolesQuery: useGetAvailableRolesQuery,

  // Templates management
  useGetV2ProviderOrganizationsTemplatesQuery: useGetTemplatesQuery,
  usePostV2ProviderOrganizationsTemplatesMutation: useAddTemplateMutation,
  usePutV2ProviderOrganizationsTemplatesByTemplateIdMutation: useUpdateTemplateMutation,
  useDeleteV2ProviderOrganizationsTemplatesByTemplateIdMutation: useRemoveTemplateMutation,

  // Forms management
  useGetV2ProviderOrganizationsFormsQuery: useGetFormsQuery,
  usePostV2ProviderOrganizationsFormsMutation: useAddFormMutation,
  useDeleteV2ProviderOrganizationsFormsByFormIdMutation: useRemoveFormMutation,

  // Patient visibility settings
  useGetV2ProviderOrganizationsPatientVisibilityQuery: useGetPatientVisibilityQuery,
  usePatchV2ProviderOrganizationsPatientVisibilityMutation: useUpdatePatientVisibilityMutation,

  // Permissions matrix (ADR 0073)
  useGetV2ProviderOrganizationsPermissionsQuery: useGetPermissionsMatrixQuery,
  usePatchV2ProviderOrganizationsPermissionsMutation: useUpdatePermissionsMatrixMutation,
} = organizationsApi;

// Re-export types for convenience
export type {
  MemberBillingInfo,
  MemberDetailResponse,
  MembershipInfoResponse,
  ProviderInvitationAcceptRequest,
  ProviderInvitationAcceptResponse,
  ProviderInvitationValidationResponse,
  ProviderLicenseType,
  ProviderPermissionMatrixItem,
  // Permissions matrix (ADR 0073) - now emitted by codegen from the backend contract
  ProviderPermissionRoleGrant,
  ProviderPermissionsMatrixResponse,
  ProviderUpdatePermissionsMatrixRequest,
  UpdateMemberBillingInfoRequest,
  UpdateMemberProfileRequest,
  UpdateMembershipInfoRequest,
} from '@store/generated/organizations';
